import React from 'react';
import { Wind, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const packages = [
  {
    btu: '9000',
    subtitle: 'Small Rooms & Bedrooms',
    price: 'R2,999',
    popular: false,
    whatsapp: 'Hi%2C%20I%27d%20like%20a%20quote%20for%20the%209000%20BTU%20installation%20special',
  },
  {
    btu: '12000',
    subtitle: 'Medium Rooms & Lounges',
    price: 'R3,499',
    popular: true,
    whatsapp: 'Hi%2C%20I%27d%20like%20a%20quote%20for%20the%2012000%20BTU%20installation%20special',
  },
  {
    btu: '18000',
    subtitle: 'Large Rooms & Open Plan',
    price: 'R3,999',
    popular: false,
    whatsapp: 'Hi%2C%20I%27d%20like%20a%20quote%20for%20the%2018000%20BTU%20installation%20special',
  },
];

const features = [
  'Standard back-to-back installation',
  'Neat copper piping & trunking',
  'Professional electrical connection',
  'Full system testing',
  'Handover & walkthrough',
];

export const InstallationPackages = () => {
  return (
    <section data-testid="installation-section" className="py-20 bg-[#111640] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#0066FF]/5 rounded-full blur-[120px]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <span className="inline-block bg-[#0066FF]/20 text-[#00D9FF] px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
            Premium Installations
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Professional AC Installation Packages
          </h2>
          <p className="text-base md:text-lg text-gray-400 max-w-3xl mx-auto">
            Professional split system installations. Smart diagnostics. Premium workmanship. Starting from R2,999.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto mb-12">
          {packages.map((pkg) => (
            <Card
              key={pkg.btu}
              data-testid={`package-${pkg.btu}`}
              className={`relative transition-all duration-300 transform hover:-translate-y-2 ${
                pkg.popular
                  ? 'bg-gradient-to-br from-[#0066FF] to-[#00D9FF] border-0 shadow-2xl shadow-blue-500/20 scale-[1.02] md:scale-105'
                  : 'bg-white/5 backdrop-blur-md border border-white/10 hover:border-[#00D9FF]/30'
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-[#0A0E27] text-[#00D9FF] px-5 py-1.5 rounded-full text-xs font-bold shadow-xl border border-[#00D9FF]/30 uppercase tracking-wider">
                    Most Popular
                  </span>
                </div>
              )}
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                    pkg.popular ? 'bg-white/20' : 'bg-white/5 border border-white/10'
                  }`}>
                    <Wind className={`w-7 h-7 ${pkg.popular ? 'text-white' : 'text-[#00D9FF]'}`} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">{pkg.btu} BTU</h3>
                  <p className={`text-sm mb-4 ${pkg.popular ? 'text-blue-100' : 'text-gray-400'}`}>{pkg.subtitle}</p>
                  <div className="text-4xl md:text-5xl font-black text-white">{pkg.price}</div>
                  <p className={`text-sm mt-2 ${pkg.popular ? 'text-blue-100' : 'text-gray-500'}`}>Complete Installation</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {features.map((f, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <CheckCircle2 className={`w-5 h-5 mr-3 flex-shrink-0 mt-0.5 ${pkg.popular ? 'text-white' : 'text-[#00D9FF]'}`} />
                      <span className={pkg.popular ? 'text-white' : 'text-gray-300'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button className={`w-full py-6 text-lg rounded-xl font-bold transition-all ${
                  pkg.popular
                    ? 'bg-white text-[#0A0E27] hover:bg-gray-100 shadow-xl'
                    : 'bg-gradient-to-r from-[#0066FF] to-[#00D9FF] hover:from-[#0052CC] hover:to-[#00B8D4] text-white'
                }`}>
                  <a
                    href={`https://wa.me/27644505026?text=${pkg.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-2"
                  >
                    <Send className="w-5 h-5" />
                    <span>{pkg.popular ? 'Get Quote Now' : 'Get Quote'}</span>
                  </a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white/5 backdrop-blur-md border border-white/10 p-6 rounded-xl">
            <p className="text-gray-300 leading-relaxed">
              <strong className="text-[#00D9FF]">Professional Installation:</strong> All installations include standard back-to-back setup with neat trunking and professional electrical connections.
            </p>
            <p className="text-sm text-gray-500 mt-3">
              <strong>Note:</strong> Limited time offer. Standard installations only. Long-run piping quoted separately.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
